import React, { useEffect, useState } from "react";
import Layout from "../../components/layout";
import { FaArrowLeft, FaSearch } from "react-icons/fa";
import { MdCheckBoxOutlineBlank } from "react-icons/md";
import { ImPrinter } from "react-icons/im";

import styles from "./OpenSampleListPage.module.scss";
import { Link, useNavigate } from "react-router-dom";
import { route } from "../../static/route";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import ListCard from "../../components/samples/ListCard";
import { FaSquareCheck } from "react-icons/fa6";
import { POST } from "../../services/axios.service";
import {
  fetchOpenSamplesData,
  getSamples,
  getSearch,
  getTotalSamples,
  updateSearch,
} from "../../features/openSample/openSampleSlice";
import { SampleProps } from "../../types/sample";

const OpenSampleListPage = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  let debounceTimeout: NodeJS.Timeout;
  const samples = useAppSelector(getSamples);
  const totalSamples = useAppSelector(getTotalSamples);
  const search = useAppSelector(getSearch);

  const [page, setPage] = useState<number>(1);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [firstLoading, setFirstLoading] = useState<boolean>(false);
  const [printSamples, setPrintSamples] = useState<SampleProps[]>([]);
  const [showFilter, setShowFilter] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleGoToBack = () => {
    navigate(route.dashboard);
  };

  const handleChangeSearch = (search: string | null) => {
    dispatch(updateSearch(search));
    setPage(1);
  };

  const handleChangePrintSample = (sample: SampleProps, action: boolean) => {
    setPrintSamples((prevSamples) => {
      if (action) {
        if (
          !prevSamples.some((existingSample) => existingSample.id === sample.id)
        ) {
          return [...prevSamples, sample];
        }
        return prevSamples;
      } else {
        return prevSamples.filter(
          (existingSample) => existingSample.id !== sample.id
        );
      }
    });
  };

  const handlePrintReport = async () => {
    setErrorMessage(null);
    try {
      await POST(`/v1/worker/sample-reports`, {
        sample_ids: printSamples.map((sample) => sample.id) || [],
      })
        .then(async (response: any) => {
          if (response.data.status === "success") {
            setPrintSamples([]);
            const downloadLink = response.data.data.download_link;
            try {
              const newWindow = window.open(downloadLink, "_blank");
              if (
                !newWindow ||
                newWindow.closed ||
                typeof newWindow.closed == "undefined"
              ) {
                throw new Error("પોપ્અપ બ્લોક કરેલ છે.");
              }
            } catch (e) {
              window.location.href = downloadLink;
            }
          } else {
            setErrorMessage(response.data.message);
          }
        })
        .catch((error: any) => {
          setErrorMessage(error.data.message);
        });
    } catch (error) {
      setErrorMessage("કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.");
    }
  };

  useEffect(() => {
    if (page === 1) setFirstLoading(true);
    setIsLoading(true);

    dispatch(
      fetchOpenSamplesData({
        page: page,
        search: search || undefined,
      })
    ).then(() => {
      setIsLoading(false);
      setFirstLoading(false);
    });
  }, [page, search]);

  return (
    <Layout>
      <div className={styles.OpenSampleListPage}>
        <div className="w-full px-10 h-[200px] text-center flex flex-col justify-center relative">
          <FaArrowLeft
            onClick={handleGoToBack}
            className="absolute top-5 left-5 text-3xl text-white border-2 border-white p-1 rounded-full"
          />

          <h1 className="text-4xl mt-6 font-bold text-white">
            સેમ્પલ - ચાલુ કામ
          </h1>

          <div className="absolute top-5 right-5 flex gap-3">
            {printSamples.length > 0 && (
              <ImPrinter
                onClick={handlePrintReport}
                className="text-3xl text-white"
              />
            )}
          </div>
        </div>

        <div className="w-full relative mt-[70px] px-10 py-5 lg:mt-[20px]">
          {errorMessage && (
            <p className="text-red-500 text-sm font-semibold text-center mb-5">
              {errorMessage}
            </p>
          )}

          <div className="flex mb-5">
            <button
              className="p-2 w-fit ml-auto rounded-md flex items-center bg-gradient-to-r from-primary-1 to-primary-2 text-white transition-colors duration-300"
              onClick={() => setShowFilter(!showFilter)}
              type="button"
            >
              <FaSearch className="mr-2" />{" "}
              {showFilter ? "ફિલ્ટર છુપાવો" : "ફિલ્ટર બતાવો"}
            </button>
          </div>

          {showFilter && (
            <div className="bg-gradient-to-r mb-10 from-primary-1 to-primary-2 h-16 w-full relative rounded">
              <input
                defaultValue={search || ""}
                className="text-primary-1 px-3 py-1 font-semibold text-xl placeholder:text-primary-2 absolute top-[2px] left-[2px] w-[calc(100%-4px)] h-[calc(100%-4px)] rounded"
                placeholder="વિગતો દ્વારા સેર્ચ કરો"
                type="text"
                onKeyUp={(e: any) => {
                  const value = e.target.value;

                  clearTimeout(debounceTimeout);
                  debounceTimeout = setTimeout(() => {
                    handleChangeSearch(value || null);
                  }, 2000);
                }}
                onFocus={(e) =>
                  e.target.addEventListener(
                    "wheel",
                    function (e) {
                      e.preventDefault();
                    },
                    { passive: false }
                  )
                }
              />
            </div>
          )}

          {firstLoading ? (
            <p className="mt-4 text-primary-2 font-bold text-2xl text-center">
              રાહ જુઓ...
            </p>
          ) : (
            <>
              {samples && samples.length > 0 ? (
                <div>
                  {samples.map((sample, index) => (
                    <div className="relative w-full h-full" key={index}>
                      <ListCard sample={sample} />

                      <div className="absolute top-3 right-3 flex gap-3">
                        {printSamples.some((o) => o.id === sample.id) ? (
                          <div>
                            <ImPrinter className="text-xl text-primary-1" />
                            <FaSquareCheck
                              onClick={(e: any) => {
                                e.preventDefault();
                                handleChangePrintSample(sample, false);
                              }}
                              className="text-xl text-primary-1"
                            />
                          </div>
                        ) : (
                          <div>
                            <ImPrinter className="text-xl text-primary-1" />
                            <MdCheckBoxOutlineBlank
                              onClick={(e: any) => {
                                e.preventDefault();
                                handleChangePrintSample(sample, true);
                              }}
                              className="text-xl text-primary-1"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center">
                  <p className="mt-4 text-primary-2 font-bold text-2xl">
                    હાલ કોઈ કામ ની વિગતો મળી નથી.
                  </p>

                  <div className="w-full flex justify-center">
                    <Link to={route.newSample}>
                      <button
                        type="button"
                        className="mx-auto mt-8 shadow rounded-3xl text-3xl text-white bg-gradient-to-r from-primary-1 to-primary-2 font-bold px-10 py-2"
                      >
                        કામ ઉમેરો
                      </button>
                    </Link>
                  </div>
                </div>
              )}

              {totalSamples > 0 && totalSamples > samples.length && (
                <div className="flex">
                  <button
                    onClick={() => setPage(page + 1)}
                    disabled={isLoading}
                    type="button"
                    className="mx-auto mt-8 shadow rounded-3xl text-3xl text-white bg-gradient-to-r from-primary-1 to-primary-2 font-bold px-10 py-2"
                  >
                    {isLoading ? "રાહ જુઓ..." : "વધુ જુઓ"}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </Layout>
  );
};
export default OpenSampleListPage;
